import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { getPendingCredentials, verifyUserCredentials, rejectUserCredentials, testEmailConfig, sendTestEmail } from '@/services/api';
import { toast } from 'sonner';
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  User, 
  Mail, 
  Phone,
  Calendar,
  FileText,
  ExternalLink,
  AlertCircle,
  Scissors,
  Sparkles,
  Star,
  Send,
  Settings
} from 'lucide-react';

const LICENSE_ICONS = {
  cosmetology: Scissors,
  barber: Scissors,
  nails: Sparkles,
  esthetician: Star
};

const LICENSE_LABELS = {
  cosmetology: 'Cosmetology',
  barber: 'Barber',
  nails: 'Nail Technician',
  esthetician: 'Esthetician'
};

export default function AdminCredentialsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectReason, setRejectReason] = useState({});
  const [showRejectInput, setShowRejectInput] = useState({});
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [emailTestLoading, setEmailTestLoading] = useState(false);

  const isAdmin = user?.email === 'scott@badasshair.com' || user?.is_admin;

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Access denied');
      navigate('/dashboard');
      return;
    }

    const fetchPending = async () => {
      try {
        const data = await getPendingCredentials();
        setPendingUsers(data);
      } catch (error) {
        console.error('Error fetching pending credentials:', error);
        if (error.response?.status === 403) {
          toast.error('Admin access required');
          navigate('/dashboard');
        } else {
          toast.error('Failed to load pending verifications');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPending();
  }, [isAdmin, navigate]);

  const handleVerify = async (userId, email) => {
    setActionLoading(`verify-${userId}`);
    try {
      await verifyUserCredentials(userId);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
      toast.success(`${email} has been verified!`);
    } catch (error) {
      toast.error('Failed to verify user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId, email) => {
    const reason = rejectReason[userId] || 'Invalid or unclear credentials';
    setActionLoading(`reject-${userId}`);
    try {
      await rejectUserCredentials(userId, reason);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
      toast.success(`${email} has been rejected`);
    } catch (error) {
      toast.error('Failed to reject user');
    } finally {
      setActionLoading(null);
      setShowRejectInput({ ...showRejectInput, [userId]: false });
    }
  };

  const handleTestEmail = async () => {
    setEmailTestLoading(true);
    setEmailTestResult(null);
    try {
      const result = await testEmailConfig();
      setEmailTestResult(result);
      if (result.auth_status === 'authenticated') {
        toast.success('Email configuration is working!');
      } else {
        toast.error(result.error || 'Email configuration issue detected');
      }
    } catch (error) {
      setEmailTestResult({ error: error.message || 'Failed to test email config' });
      toast.error('Failed to test email configuration');
    } finally {
      setEmailTestLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    setEmailTestLoading(true);
    try {
      const result = await sendTestEmail();
      if (result.status === 'success') {
        toast.success('Test email sent! Check your inbox.');
      } else {
        toast.error(result.message || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setEmailTestLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAdmin) return null;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97757]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-semibold text-stone-900">
                Credential Verification
              </h1>
              <p className="text-stone-600">Review and approve new user credentials</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <Card className="border-stone-100">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{pendingUsers.length}</p>
                  <p className="text-sm text-stone-600">Pending Verifications</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-stone-100">
            <CardContent className="p-6">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-4 w-full text-left hover:opacity-80 transition-opacity"
              >
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-stone-900">Reported Reviews</p>
                  <p className="text-sm text-stone-600">Go to review moderation →</p>
                </div>
              </button>
            </CardContent>
          </Card>
        </div>

        {/* Email Test Section */}
        <Card className="border-stone-100 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900">Email Configuration</h3>
                <p className="text-sm text-stone-600">Test if notification emails are working</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3 mb-4">
              <Button
                onClick={handleTestEmail}
                disabled={emailTestLoading}
                variant="outline"
                className="h-10 px-4 rounded-lg"
                data-testid="test-email-config-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                {emailTestLoading ? 'Testing...' : 'Test Configuration'}
              </Button>
              
              <Button
                onClick={handleSendTestEmail}
                disabled={emailTestLoading}
                className="h-10 px-4 rounded-lg bg-purple-600 text-white hover:bg-purple-700"
                data-testid="send-test-email-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {emailTestLoading ? 'Sending...' : 'Send Test Email'}
              </Button>
            </div>
            
            {emailTestResult && (
              <div className={`p-4 rounded-lg ${emailTestResult.auth_status === 'authenticated' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-start gap-2">
                  {emailTestResult.auth_status === 'authenticated' ? (
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  )}
                  <div className="text-sm">
                    <p className={`font-medium ${emailTestResult.auth_status === 'authenticated' ? 'text-green-800' : 'text-red-800'}`}>
                      {emailTestResult.auth_status === 'authenticated' ? 'Email is configured correctly!' : 'Email configuration issue'}
                    </p>
                    {emailTestResult.error && (
                      <p className="text-red-700 mt-1">{emailTestResult.error}</p>
                    )}
                    <div className="mt-2 text-stone-600 space-y-1">
                      <p><strong>Server:</strong> {emailTestResult.smtp_host}:{emailTestResult.smtp_port}</p>
                      <p><strong>Sender:</strong> {emailTestResult.sender_email}</p>
                      <p><strong>Connection:</strong> {emailTestResult.connection_status}</p>
                      <p><strong>Authentication:</strong> {emailTestResult.auth_status}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Users List */}
        {pendingUsers.length === 0 ? (
          <Card className="border-stone-100">
            <CardContent className="p-12 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-stone-900 mb-2">All Caught Up!</h3>
              <p className="text-stone-600">No pending credential verifications at this time.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {pendingUsers.map((pendingUser) => {
              const LicenseIcon = LICENSE_ICONS[pendingUser.license_type] || FileText;
              
              return (
                <Card key={pendingUser.id} className="border-stone-100 shadow-sm overflow-hidden">
                  <div className="bg-amber-50 px-6 py-3 border-b border-amber-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-amber-700">
                        <AlertCircle className="w-4 h-4" />
                        <span className="font-medium">Pending Verification</span>
                      </div>
                      <span className="text-sm text-amber-600">
                        Registered {formatDate(pendingUser.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* User Info */}
                      <div>
                        <h4 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
                          <User className="w-4 h-4" />
                          User Information
                        </h4>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                            <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center">
                              <span className="text-stone-600 font-semibold">
                                {pendingUser.full_name?.charAt(0) || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-stone-900">{pendingUser.full_name}</p>
                              <p className="text-sm text-stone-600 flex items-center gap-1">
                                <Mail className="w-3 h-3" /> {pendingUser.email}
                              </p>
                            </div>
                          </div>
                          
                          {pendingUser.phone && (
                            <p className="text-sm text-stone-600 flex items-center gap-2 px-3">
                              <Phone className="w-4 h-4" /> {pendingUser.phone}
                            </p>
                          )}
                          
                          {pendingUser.salon_name && (
                            <p className="text-sm text-stone-600 px-3">
                              <strong>Salon:</strong> {pendingUser.salon_name}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* License Info */}
                      <div>
                        <h4 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
                          <LicenseIcon className="w-4 h-4" />
                          License Credentials
                        </h4>
                        <div className="p-4 bg-stone-50 rounded-lg space-y-3">
                          <div className="flex items-center gap-2">
                            <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                              {LICENSE_LABELS[pendingUser.license_type] || pendingUser.license_type}
                            </span>
                          </div>
                          
                          {/* Professional License */}
                          <div className="border-b border-stone-200 pb-3">
                            <p className="text-sm font-medium text-stone-700 mb-2">📋 Professional License:</p>
                            {pendingUser.license_url ? (
                              <div>
                                <a
                                  href={pendingUser.license_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  View Full Image
                                </a>
                                <div className="mt-2">
                                  <img 
                                    src={pendingUser.license_url} 
                                    alt="Professional License" 
                                    className="max-w-full max-h-40 rounded-lg border border-stone-200 object-contain"
                                  />
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-red-600 flex items-center gap-2">
                                <XCircle className="w-4 h-4" />
                                No license uploaded
                              </p>
                            )}
                          </div>
                          
                          {/* ID / Driver's License */}
                          <div className="pt-2">
                            <p className="text-sm font-medium text-stone-700 mb-2">🪪 Driver&apos;s License / ID:</p>
                            {pendingUser.id_url ? (
                              <div>
                                <a
                                  href={pendingUser.id_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  View Full Image
                                </a>
                                <div className="mt-2">
                                  <img 
                                    src={pendingUser.id_url} 
                                    alt="ID Document" 
                                    className="max-w-full max-h-40 rounded-lg border border-stone-200 object-contain"
                                  />
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-red-600 flex items-center gap-2">
                                <XCircle className="w-4 h-4" />
                                No ID uploaded
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 pt-6 border-t border-stone-200">
                      <h4 className="font-medium text-stone-900 mb-3">Actions</h4>
                      
                      {showRejectInput[pendingUser.id] ? (
                        <div className="space-y-3">
                          <Input
                            placeholder="Reason for rejection (optional)"
                            value={rejectReason[pendingUser.id] || ''}
                            onChange={(e) => setRejectReason({ ...rejectReason, [pendingUser.id]: e.target.value })}
                            className="max-w-md"
                          />
                          <div className="flex gap-3">
                            <Button
                              onClick={() => setShowRejectInput({ ...showRejectInput, [pendingUser.id]: false })}
                              variant="outline"
                              className="h-10 px-4 rounded-lg"
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => handleReject(pendingUser.id, pendingUser.email)}
                              disabled={actionLoading === `reject-${pendingUser.id}`}
                              className="h-10 px-4 rounded-lg bg-red-600 text-white hover:bg-red-700"
                            >
                              {actionLoading === `reject-${pendingUser.id}` ? 'Rejecting...' : 'Confirm Rejection'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-3">
                          <Button
                            onClick={() => handleVerify(pendingUser.id, pendingUser.email)}
                            disabled={actionLoading === `verify-${pendingUser.id}`}
                            className="h-10 px-6 rounded-lg bg-green-600 text-white hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {actionLoading === `verify-${pendingUser.id}` ? 'Approving...' : 'Approve'}
                          </Button>
                          
                          <Button
                            onClick={() => setShowRejectInput({ ...showRejectInput, [pendingUser.id]: true })}
                            variant="outline"
                            className="h-10 px-6 rounded-lg border-red-300 text-red-700 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
